const isRebuyCartExperience = () => window.cartExperience?.mode === 'rebuy';

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeAnimationTimeout = null;
    this.openAnimationTimeout = null;
    this.recommendationRefreshTimeout = null;
    this.deferDesktopRecommendationOpen = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayCloseHandler();
    this.setHeaderCartIconAccessibility();

    if (isRebuyCartExperience()) {
      this.initRebuyCoexistence();
    }
  }

  initRebuyCoexistence() {
    this.handleRebuyLinkClick = this.handleRebuyLinkClick.bind(this);
    this.handleRebuyCartOpen = this.handleRebuyCartOpen.bind(this);

    document.addEventListener('click', this.handleRebuyLinkClick, { passive: true });
    document.addEventListener('rebuy:cart:open', this.handleRebuyCartOpen);

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (form && form.action && form.action.includes('/cart/add')) {
          try {
            localStorage.setItem('rebuy_should_open_cart', 'true');
          } catch (e) {
            console.warn('Could not set rebuy_should_open_cart flag', e);
          }
        }
      },
      { passive: true }
    );
  }

  handleRebuyCartOpen() {
    if (document.body.classList.contains('overflow-hidden') && this.classList.contains('active')) {
      this.classList.remove('active');
    }
  }

  handleRebuyLinkClick(event) {
    const linkEl = event.target.closest('a');

    if (
      !linkEl ||
      linkEl.closest('form') ||
      linkEl.closest('button') ||
      linkEl.getAttribute('role') === 'button' ||
      linkEl.classList.contains('product-form__submit')
    ) {
      return;
    }

    if (
      linkEl.closest('[data-rebuy-id]') ||
      linkEl.closest('.rebuy-cart') ||
      linkEl.closest('.rebuy-product')
    ) {
      try {
        localStorage.setItem('rebuy_cart_cleanup_needed', 'true');
      } catch (e) {
        console.warn('Could not store rebuy cleanup flag in localStorage', e);
      }
    }
  }

  bindOverlayCloseHandler() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const wasAlreadyOpen = this.classList.contains('active');
    const wasRecommendationsActive = this.classList.contains('recommendations-active');

    if (this.closeAnimationTimeout) {
      clearTimeout(this.closeAnimationTimeout);
      this.closeAnimationTimeout = null;
    }
    if (this.openAnimationTimeout) {
      clearTimeout(this.openAnimationTimeout);
      this.openAnimationTimeout = null;
    }
    if (this.recommendationRefreshTimeout) {
      clearTimeout(this.recommendationRefreshTimeout);
      this.recommendationRefreshTimeout = null;
    }
    this.deferDesktopRecommendationOpen = false;

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const recommendationDrawer = this.querySelector('recommendation-drawer');
      const shouldPreserveDesktopRecommendationState =
        wasAlreadyOpen && wasRecommendationsActive && recommendationDrawer?.desktopMediaQuery?.matches;

      if (recommendationDrawer?.shouldStageDesktopOpen() && !shouldPreserveDesktopRecommendationState) {
        this.deferDesktopRecommendationOpen = true;
        recommendationDrawer.applyState();

        this.openAnimationTimeout = window.setTimeout(
          () => {
            this.openAnimationTimeout = null;
            this.deferDesktopRecommendationOpen = false;
            recommendationDrawer.applyState();
            this.scheduleRecommendationWidgetRefresh(
              recommendationDrawer,
              this.getTransitionTimeMs(recommendationDrawer.querySelector('.recommendation-drawer__inner'))
            );
          },
          this.getTransitionTimeMs(this.querySelector('.drawer__inner'))
        );
      } else {
        recommendationDrawer?.applyState();
        this.scheduleRecommendationWidgetRefresh(recommendationDrawer);
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this.openAnimationTimeout) {
      clearTimeout(this.openAnimationTimeout);
      this.openAnimationTimeout = null;
    }
    if (this.recommendationRefreshTimeout) {
      clearTimeout(this.recommendationRefreshTimeout);
      this.recommendationRefreshTimeout = null;
    }
    this.deferDesktopRecommendationOpen = false;

    const recommendationDrawer = this.querySelector('recommendation-drawer');
    const shouldStageClose =
      this.classList.contains('active') &&
      this.classList.contains('recommendations-active') &&
      recommendationDrawer?.desktopMediaQuery?.matches;

    if (shouldStageClose) {
      this.classList.remove('recommendations-active');
      recommendationDrawer.setAttribute('aria-hidden', 'true');

      if (this.closeAnimationTimeout) {
        clearTimeout(this.closeAnimationTimeout);
      }

      this.closeAnimationTimeout = window.setTimeout(
        () => {
          this.closeAnimationTimeout = null;
          this.finishClose();
        },
        this.getTransitionTimeMs(recommendationDrawer.querySelector('.recommendation-drawer__inner'))
      );
      return;
    }

    this.finishClose();
  }

  finishClose() {
    this.classList.remove('active', 'recommendations-active');
    this.querySelector('recommendation-drawer')?.applyState();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (isRebuyCartExperience()) {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.style.display = 'none';
        setTimeout(() => {
          overlay.style.display = '';
        }, 500);
      }
    }
  }

  scheduleRecommendationWidgetRefresh(recommendationDrawer, delayMs = 0) {
    if (!recommendationDrawer) return;

    if (this.recommendationRefreshTimeout) {
      clearTimeout(this.recommendationRefreshTimeout);
      this.recommendationRefreshTimeout = null;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.recommendationRefreshTimeout = window.setTimeout(() => {
          this.recommendationRefreshTimeout = null;
          recommendationDrawer.refreshWidget();
        }, delayMs);
      });
    });
  }

  getTransitionTimeMs(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const durations = styles.transitionDuration.split(',').map((value) => this.timeToMs(value.trim()));
    const delays = styles.transitionDelay.split(',').map((value) => this.timeToMs(value.trim()));
    const length = Math.max(durations.length, delays.length);

    return Array.from({ length }).reduce((max, _, index) => {
      const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
      return Math.max(max, duration + delay);
    }, 0);
  }

  timeToMs(value) {
    if (!value) return 0;
    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
    return Number.parseFloat(value) || 0;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const preservedRecommendationContent =
      typeof TagalysCustomisations !== 'undefined' &&
      typeof TagalysCustomisations.extractCartDrawerWidgetContent === 'function'
        ? TagalysCustomisations.extractCartDrawerWidgetContent()
        : null;

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (
      preservedRecommendationContent &&
      typeof TagalysCustomisations !== 'undefined' &&
      typeof TagalysCustomisations.restoreCartDrawerWidgetContent === 'function'
    ) {
      TagalysCustomisations.restoreCartDrawerWidgetContent(preservedRecommendationContent);
    }

    setTimeout(() => {
      this.bindOverlayCloseHandler();
      this.querySelector('recommendation-drawer')?.setPendingCartData(parsedState);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class RecommendationDrawer extends HTMLElement {
  constructor() {
    super();

    this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');
    this.pendingCartData = null;
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleShowRecommendations = this.handleShowRecommendations.bind(this);
    this.handleShowRecommendationsKeydown = this.handleShowRecommendationsKeydown.bind(this);
    this.handleHideRecommendations = this.handleHideRecommendations.bind(this);
    this.handleHideRecommendationsKeydown = this.handleHideRecommendationsKeydown.bind(this);
  }

  connectedCallback() {
    this.cartDrawer = this.closest('cart-drawer');
    this.panel = this.querySelector('[data-recommendation-panel]');
    this.desktopContainer = this.querySelector('[data-recommendation-container="desktop"]');
    this.mobileContainer = this.cartDrawer?.querySelector('[data-recommendation-container="mobile"]') || null;
    this.triggerButtons = this.cartDrawer
      ? Array.from(this.cartDrawer.querySelectorAll('[data-recommendation-trigger]'))
      : [];
    this.hideButtons = this.panel ? Array.from(this.panel.querySelectorAll('[data-recommendation-hide]')) : [];
    this.hasCartItems = Boolean(this.panel);

    this.initialiseState();
    this.bindEvents();
    this.movePanelToActiveContainer();
    this.applyState();
  }

  disconnectedCallback() {
    if (typeof this.desktopMediaQuery.removeEventListener === 'function') {
      this.desktopMediaQuery.removeEventListener('change', this.handleMediaChange);
    } else {
      this.desktopMediaQuery.removeListener(this.handleMediaChange);
    }
    this.triggerButtons.forEach((button) => button.removeEventListener('click', this.handleShowRecommendations));
    this.triggerButtons.forEach((button) =>
      button.removeEventListener('keydown', this.handleShowRecommendationsKeydown)
    );
    this.hideButtons.forEach((button) => button.removeEventListener('click', this.handleHideRecommendations));
    this.hideButtons.forEach((button) => button.removeEventListener('keydown', this.handleHideRecommendationsKeydown));
  }

  initialiseState() {
    if (!window.cartDrawerRecommendationState) {
      window.cartDrawerRecommendationState = {
        expanded: this.hasCartItems,
      };
    }

    if (!this.hasCartItems) {
      window.cartDrawerRecommendationState.expanded = false;
    }
  }

  bindEvents() {
    if (typeof this.desktopMediaQuery.addEventListener === 'function') {
      this.desktopMediaQuery.addEventListener('change', this.handleMediaChange);
    } else {
      this.desktopMediaQuery.addListener(this.handleMediaChange);
    }
    this.triggerButtons.forEach((button) => button.addEventListener('click', this.handleShowRecommendations));
    this.triggerButtons.forEach((button) => button.addEventListener('keydown', this.handleShowRecommendationsKeydown));
    this.hideButtons.forEach((button) => button.addEventListener('click', this.handleHideRecommendations));
    this.hideButtons.forEach((button) => button.addEventListener('keydown', this.handleHideRecommendationsKeydown));
  }

  handleMediaChange() {
    this.movePanelToActiveContainer();
    this.applyState();
    this.refreshWidget();
  }

  handleShowRecommendations(event) {
    event.preventDefault();
    this.setExpanded(true);
  }

  handleShowRecommendationsKeydown(event) {
    if (event.code !== 'Enter' && event.code !== 'Space' && event.code !== 'NumpadEnter') return;

    event.preventDefault();
    event.stopPropagation();
    this.handleShowRecommendations(event);
  }

  handleHideRecommendations(event) {
    event.preventDefault();
    this.setExpanded(false);
    this.focusFirstVisibleTrigger();
  }

  handleHideRecommendationsKeydown(event) {
    if (event.code !== 'Enter' && event.code !== 'Space' && event.code !== 'NumpadEnter') return;

    event.preventDefault();
    event.stopPropagation();
    this.handleHideRecommendations(event);
  }

  shouldStageDesktopOpen() {
    return (
      Boolean(window.cartDrawerRecommendationState?.expanded) && this.hasCartItems && this.desktopMediaQuery.matches
    );
  }

  setExpanded(expanded) {
    if (!this.hasCartItems) return;

    window.cartDrawerRecommendationState.expanded = expanded;
    this.movePanelToActiveContainer();
    this.applyState();

    if (expanded) {
      this.refreshWidget();
    }
  }

  setPendingCartData(cartData) {
    this.pendingCartData = cartData || null;
  }

  focusFirstVisibleTrigger() {
    const visibleTrigger = this.triggerButtons.find((button) => !button.hidden);
    visibleTrigger?.focus();
  }

  movePanelToActiveContainer() {
    if (!this.panel) return;

    const targetContainer =
      this.desktopMediaQuery.matches || !this.mobileContainer ? this.desktopContainer : this.mobileContainer;

    if (targetContainer && this.panel.parentElement !== targetContainer) {
      targetContainer.appendChild(this.panel);
    }
  }

  applyState() {
    const isExpanded = Boolean(window.cartDrawerRecommendationState?.expanded) && this.hasCartItems;
    const isDesktop = this.desktopMediaQuery.matches;
    const isDrawerOpen = this.cartDrawer?.classList.contains('active');
    const isOpeningDeferred = Boolean(this.cartDrawer?.deferDesktopRecommendationOpen);
    const showDesktopDrawer = isExpanded && isDesktop && isDrawerOpen && !isOpeningDeferred;
    const isVisible = isDesktop ? showDesktopDrawer : this.hasCartItems && isExpanded;
    const shouldRenderDesktopShell = this.hasCartItems && (!isDesktop || isDrawerOpen);
    const shouldRenderPanel = this.hasCartItems && (isDesktop ? shouldRenderDesktopShell : isExpanded);

    this.classList.toggle('cart-is-empty', !this.hasCartItems);
    this.classList.toggle('is-active', showDesktopDrawer);
    this.hidden = !shouldRenderDesktopShell;
    this.setAttribute('aria-hidden', String(!isVisible));
    this.cartDrawer?.classList.toggle('recommendations-active', showDesktopDrawer);

    this.triggerButtons.forEach((button) => {
      button.hidden = !this.hasCartItems || isExpanded;
      button.setAttribute('aria-expanded', String(isExpanded));
    });

    this.hideButtons.forEach((button) => {
      button.hidden = !shouldRenderPanel;
      button.setAttribute('aria-expanded', String(isExpanded));
    });

    if (this.mobileContainer) {
      this.mobileContainer.hidden = !this.hasCartItems || !isExpanded || isDesktop;
    }

    if (this.panel) {
      this.panel.hidden = !shouldRenderPanel;
    }
  }

  refreshWidget() {
    const isDrawerOpen = this.cartDrawer?.classList.contains('active');
    if (!this.hasCartItems || !window.cartDrawerRecommendationState?.expanded || !isDrawerOpen) return;

    if (
      typeof TagalysCustomisations !== 'undefined' &&
      typeof TagalysCustomisations.scheduleCartDrawerWidgetRefresh === 'function'
    ) {
      TagalysCustomisations.scheduleCartDrawerWidgetRefresh({ cartData: this.pendingCartData || undefined });
      this.pendingCartData = null;
    }
  }
}

customElements.define('recommendation-drawer', RecommendationDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
